import { apiGet, apiPost, apiPatch, apiDelete } from './api-client';
import type {
  Habit,
  HabitWithStreak,
  HabitCompletion,
  UserGamification,
  Achievement,
  UserAchievement,
  CreateHabitInput,
  UpdateHabitInput,
  CoachingCategory,
} from '@/types';

// ============================================
// Habit CRUD Operations
// ============================================

/**
 * Create a new habit
 */
export async function createHabit(input: CreateHabitInput): Promise<Habit> {
  return apiPost<Habit>('/api/habits', input);
}

/**
 * Get all habits for current user with streak data
 * API returns habits with streak and completedToday already calculated
 */
export async function getHabits(): Promise<HabitWithStreak[]> {
  return apiGet<HabitWithStreak[]>('/api/habits');
}

/**
 * Get habits filtered by category
 */
export async function getHabitsByCategory(category: CoachingCategory): Promise<HabitWithStreak[]> {
  return apiGet<HabitWithStreak[]>(`/api/habits?category=${category}`);
}

/**
 * Update a habit
 */
export async function updateHabit(id: string, updates: UpdateHabitInput): Promise<Habit> {
  return apiPatch<Habit>(`/api/habits/${id}`, updates);
}

/**
 * Delete a habit permanently
 */
export async function deleteHabit(id: string): Promise<void> {
  await apiDelete(`/api/habits/${id}`);
}

/**
 * Archive a habit (soft delete)
 */
export async function archiveHabit(id: string): Promise<void> {
  await apiPatch(`/api/habits/${id}`, { isActive: false });
}

// ============================================
// Habit Completions
// ============================================

/**
 * Complete a habit check-in
 */
export async function completeHabit(
  habitId: string,
  notes?: string,
  moodRating?: number
): Promise<HabitCompletion> {
  // Backend returns: { completion, streak, xpEarned, newAchievements }
  // We only need the completion object
  const response = await apiPost<{
    completion: HabitCompletion;
    streak: number;
    xpEarned: number;
    newAchievements: any[];
  }>(`/api/habits/${habitId}/complete`, { notes, moodRating });

  return response.completion;
}

/**
 * Get completions for a specific habit
 */
export async function getCompletionsForHabit(
  habitId: string,
  days: number = 30
): Promise<HabitCompletion[]> {
  return apiGet<HabitCompletion[]>(`/api/habits/${habitId}/completions?days=${days}`);
}

// ============================================
// Gamification Stats
// ============================================

/**
 * Get user's gamification stats
 */
export async function getUserGamification(): Promise<UserGamification> {
  return apiGet<UserGamification>('/api/gamification');
}

/**
 * Get all achievements
 */
export async function getAchievements(): Promise<Achievement[]> {
  return apiGet<Achievement[]>('/api/achievements');
}

/**
 * Get user's unlocked achievements
 */
export async function getUserAchievements(): Promise<UserAchievement[]> {
  return apiGet<UserAchievement[]>('/api/achievements/unlocked');
}

// ============================================
// Realtime Subscriptions
// ============================================

/**
 * Subscribe to habit completion updates (no-op: realtime via SSE not yet wired)
 */
export function subscribeToCompletions(
  _userId: string,
  _onCompletion: (completion: HabitCompletion) => void
) {
  return () => {};
}

/**
 * Subscribe to gamification updates (no-op: realtime via SSE not yet wired)
 */
export function subscribeToGamification(
  _userId: string,
  _onUpdate: (stats: UserGamification) => void
) {
  return () => {};
}

/**
 * Subscribe to new achievement unlocks (no-op: realtime via SSE not yet wired)
 */
export function subscribeToAchievements(
  _userId: string,
  _onUnlock: (achievement: UserAchievement) => void
) {
  return () => {};
}
